import { useEffect, useState } from "react";
import client, { fmt } from "@/lib/api";
import { Panel } from "@/components/Layout";
import { Search, Megaphone, Tv, Mail, Sparkles, Globe, Plus, Trash2, Activity } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";

const CHANNELS = [
    { key: "search", label: "Search", icon: Search },
    { key: "social", label: "Social", icon: Megaphone },
    { key: "display", label: "Display", icon: Tv },
    { key: "email", label: "Email", icon: Mail },
    { key: "influencer", label: "Influencer", icon: Sparkles },
    { key: "seo", label: "SEO", icon: Globe },
];

const OBJECTIVES = ["awareness", "conversion", "retention", "lineage_build"];

export default function CampaignHUD() {
    const [campaigns, setCampaigns] = useState([]);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "", channel: "social", objective: "awareness",
        budget: "", spend: "", revenue: "",
        impressions: "", clicks: "", conversions: "", status: "active",
    });

    const load = async () => {
        const { data } = await client.get("/campaigns");
        setCampaigns(data);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await client.post("/campaigns", {
                ...form,
                budget: parseFloat(form.budget) || 0,
                spend: parseFloat(form.spend) || 0,
                revenue: parseFloat(form.revenue) || 0,
                impressions: parseInt(form.impressions) || 0,
                clicks: parseInt(form.clicks) || 0,
                conversions: parseInt(form.conversions) || 0,
            });
            toast.success("Campaign launched into the HUD");
            setOpen(false);
            setForm({ name: "", channel: "social", objective: "awareness", budget: "", spend: "", revenue: "", impressions: "", clicks: "", conversions: "", status: "active" });
            load();
        } catch {
            toast.error("Launch failed");
        }
    };

    const remove = async (id) => {
        await client.delete(`/campaigns/${id}`);
        toast("Campaign decommissioned");
        load();
    };

    const filtered = filter === "all" ? campaigns : campaigns.filter((c) => c.channel === filter);
    const counts = CHANNELS.reduce((acc, c) => {
        acc[c.key] = campaigns.filter((p) => p.channel === c.key).length;
        return acc;
    }, {});

    return (
        <div className="space-y-6" data-testid="campaign-hud">
            <header className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-tag mb-2">/// MODULE 01 — UNIFIED CAMPAIGN TELEMETRY</div>
                    <h1 className="heading-display text-4xl sm:text-5xl">Campaign HUD</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button className="btn-gold inline-flex items-center gap-2" data-testid="new-campaign-btn">
                            <Plus className="w-4 h-4" /> Launch Campaign
                        </button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0a0a0a] border border-gold text-white max-w-xl rounded-none">
                        <DialogHeader>
                            <DialogTitle className="heading-display text-2xl">Launch Campaign</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="campaign-form">
                            <Field label="Campaign Name">
                                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="hud-input" data-testid="campaign-name" />
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Channel">
                                    <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="hud-input" data-testid="campaign-channel">
                                        {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Objective">
                                    <select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="hud-input" data-testid="campaign-objective">
                                        {OBJECTIVES.map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                </Field>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="Budget ($)"><input type="number" required value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="hud-input" data-testid="campaign-budget" /></Field>
                                <Field label="Spend ($)"><input type="number" value={form.spend} onChange={(e) => setForm({ ...form, spend: e.target.value })} className="hud-input" data-testid="campaign-spend" /></Field>
                                <Field label="Revenue ($)"><input type="number" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} className="hud-input" data-testid="campaign-revenue" /></Field>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="Impressions"><input type="number" value={form.impressions} onChange={(e) => setForm({ ...form, impressions: e.target.value })} className="hud-input" data-testid="campaign-impressions" /></Field>
                                <Field label="Clicks"><input type="number" value={form.clicks} onChange={(e) => setForm({ ...form, clicks: e.target.value })} className="hud-input" data-testid="campaign-clicks" /></Field>
                                <Field label="Conversions"><input type="number" value={form.conversions} onChange={(e) => setForm({ ...form, conversions: e.target.value })} className="hud-input" data-testid="campaign-conversions" /></Field>
                            </div>
                            <button className="btn-gold w-full" type="submit" data-testid="campaign-submit">Launch</button>
                        </form>
                        <style>{`.hud-input { background:#050505; border:1px solid #332A0C; padding:10px 12px; width:100%; color:#F5F5F5; font-family:'Montserrat',sans-serif; outline:none; } .hud-input:focus { border-color:#D4AF37; }`}</style>
                    </DialogContent>
                </Dialog>
            </header>

            {/* Channel tabs */}
            <div className="grid grid-cols-2 lg:grid-cols-7 gap-3" data-testid="channel-tabs">
                <ChannelTab active={filter === "all"} onClick={() => setFilter("all")} label="All" count={campaigns.length} testId="tab-all" />
                {CHANNELS.map((c) => (
                    <ChannelTab key={c.key} active={filter === c.key} onClick={() => setFilter(c.key)} label={c.label} count={counts[c.key]} Icon={c.icon} testId={`tab-${c.key}`} />
                ))}
            </div>

            {/* Telemetry grid */}
            <Panel tag={`// ${filtered.length} ACTIVE VESSELS`} title="Campaign Telemetry" testId="hud-table">
                <div className="grid lg:grid-cols-2 gap-3" data-testid="campaign-grid">
                    {filtered.map((c) => {
                        const roas = c.spend > 0 ? c.revenue / c.spend : 0;
                        const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100) : 0;
                        const cvr = c.clicks > 0 ? (c.conversions / c.clicks * 100) : 0;
                        const Icon = CHANNELS.find((ch) => ch.key === c.channel)?.icon || Globe;
                        return (
                            <div key={c.id} className="border border-gold-edge p-4 hover:border-gold transition-colors" data-testid={`campaign-${c.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="inline-flex items-center gap-2 label-tag mb-1">
                                            <Icon className="w-3 h-3" /> {c.channel} · {c.objective}
                                        </div>
                                        <div className="heading-display text-lg leading-tight">{c.name}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`label-tag px-2 py-1 border ${c.status === 'active' ? 'border-[#34C759] text-[#34C759]' : 'border-white/30 text-white/50'}`}>{c.status}</span>
                                        <button onClick={() => remove(c.id)} className="text-[#666] hover:text-[#FF3B30]" data-testid={`delete-campaign-${c.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-4">
                                    <Stat label="ROAS" value={`${roas.toFixed(2)}×`} accent />
                                    <Stat label="CTR" value={`${ctr.toFixed(2)}%`} />
                                    <Stat label="CVR" value={`${cvr.toFixed(2)}%`} />
                                </div>
                                <div className="circuit-divider my-3"></div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <Mini label="Spend" value={fmt.money(c.spend)} />
                                    <Mini label="Revenue" value={fmt.money(c.revenue)} />
                                    <Mini label="Impressions" value={c.impressions.toLocaleString()} />
                                    <Mini label="Conversions" value={c.conversions.toLocaleString()} />
                                </div>
                            </div>
                        );
                    })}
                    {filtered.length === 0 && (
                        <div className="col-span-2 text-center py-10 text-[#666]" data-testid="empty-campaigns">
                            <Activity className="w-8 h-8 text-gold mx-auto mb-2" />
                            <div className="label-tag">No campaigns in this channel</div>
                        </div>
                    )}
                </div>
            </Panel>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="label-tag block mb-1">{label}</label>
            {children}
        </div>
    );
}

function ChannelTab({ active, onClick, label, count, Icon, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`hud-panel p-3 text-left transition-all ${active ? "border-gold !shadow-[0_0_20px_rgba(212,175,55,0.25)]" : ""}`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="label-tag">{label}</div>
                    <div className="metric-value text-xl mt-1">{count}</div>
                </div>
                {Icon && <Icon className={`w-4 h-4 ${active ? "text-gold" : "text-[#666]"}`} />}
            </div>
        </button>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className={`border p-2 ${accent ? "border-gold bg-gold/5" : "border-gold-edge"}`}>
            <div className="label-tag">{label}</div>
            <div className={`font-black text-lg mt-1 ${accent ? "text-gold" : "text-white"}`}>{value}</div>
        </div>
    );
}

function Mini({ label, value }) {
    return (
        <div className="flex justify-between border-b border-gold-edge py-1">
            <span className="text-[10px] mono uppercase text-white/50 tracking-[0.18em]">{label}</span>
            <span className="font-bold text-white/90">{value}</span>
        </div>
    );
}
